// Projektägarens vy för att dela ut roller och behörigheter.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PROJECT_ROLES, isReadOnlyRole, type ProjectRole } from "@/lib/basProcess/roleViews";

/** Färdiga upplägg för inhyrda granskare och externa ledningsroller. */
const INVITE_TEMPLATES: {
  label: string;
  role: ProjectRole;
  permission: string;
  isDeputy: boolean;
  hint: string;
}[] = [
  {
    label: "Inhyrd granskare",
    role: "Uppdragsledare",
    permission: "read",
    isDeputy: false,
    hint: "Läser hela processen och kan godkänna eller begära komplettering – kan inte ändra dokument.",
  },
  {
    label: "Extern projektledare",
    role: "Projektledare",
    permission: "read",
    isDeputy: false,
    hint: "Ser status och granskningsöversikt för hela projektet utan redigeringsrätt.",
  },
  {
    label: "Vikarierande Bas-P",
    role: "Bas-P",
    permission: "edit",
    isDeputy: true,
    hint: "Ersättare med full redigeringsrätt i projekteringsdelen.",
  },
  {
    label: "Vikarierande Bas-U",
    role: "Bas-U",
    permission: "edit",
    isDeputy: true,
    hint: "Ersättare med full redigeringsrätt i utförandedelen.",
  },
  {
    label: "Förvaltare vid överlämning",
    role: "Förvaltare",
    permission: "read",
    isDeputy: false,
    hint: "Läser överlämnings- och bruksskedesdokument.",
  },
];
import { UserPlus, Trash2, ShieldCheck, History } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import ProjectScopeMode from "@/components/BasProcess/ProjectScopeMode";
import RoleCompetence from "@/components/BasProcess/RoleCompetence";

interface Props {
  projectId: string;
}

interface Member {
  id: string;
  shared_with_email: string;
  shared_with_id: string | null;
  project_role: string;
  permission_level: string;
  accepted: boolean;
  valid_from: string | null;
  valid_to: string | null;
  is_deputy: boolean | null;
  can_export: boolean | null;
  can_invite: boolean | null;
  can_share_external: boolean | null;
}

interface HistoryRow {
  id: string;
  action: string;
  project_role: string;
  person_email: string | null;
  reason: string | null;
  valid_from: string | null;
  valid_to: string | null;
  created_at: string;
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("sv-SE") : "–";
const toDateInput = (iso: string | null) => (iso ? iso.slice(0, 10) : "");

const ProjectMembers: React.FC<Props> = ({ projectId }) => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<ProjectRole>("Bas-U");
  const [permission, setPermission] = useState("read");
  const [isDeputy, setIsDeputy] = useState(false);
  const [validFrom, setValidFrom] = useState("");
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("project_shares")
      .select(
        "id, shared_with_email, shared_with_id, project_role, permission_level, accepted, valid_from, valid_to, is_deputy, can_export, can_invite, can_share_external"
      )
      .eq("project_id", projectId)
      .order("created_at", { ascending: true });
    if (error) toast.error("Kunde inte hämta deltagare");
    setMembers((data || []) as Member[]);
    const { data: hist } = await supabase
      .from("project_role_history")
      .select("id, action, project_role, person_email, reason, valid_from, valid_to, created_at")
      .eq("project_id", projectId)
      .order("created_at", { ascending: false })
      .limit(50);
    setHistory((hist || []) as HistoryRow[]);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const logHistory = async (
    action: string,
    member: { shared_with_email: string; shared_with_id?: string | null; project_role: string },
    extra: { reason?: string; valid_from?: string | null; valid_to?: string | null } = {}
  ) => {
    const { data: auth } = await supabase.auth.getUser();
    await supabase.from("project_role_history").insert({
      project_id: projectId,
      action,
      project_role: member.project_role,
      person_email: member.shared_with_email,
      person_id: member.shared_with_id ?? null,
      changed_by: auth.user?.id ?? null,
      reason: extra.reason ?? null,
      valid_from: extra.valid_from ?? null,
      valid_to: extra.valid_to ?? null,
    });
  };

  const invite = async () => {
    if (!email.trim()) {
      toast.error("Ange en e-postadress");
      return;
    }
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("project_shares").insert({
      project_id: projectId,
      owner_id: auth.user?.id,
      shared_with_email: email.trim().toLowerCase(),
      project_role: role,
      permission_level: isReadOnlyRole(role) ? "read" : permission,
      is_deputy: isDeputy,
      valid_from: validFrom || new Date().toISOString().slice(0, 10),
    });
    if (error) {
      toast.error("Kunde inte bjuda in");
      return;
    }
    await logHistory(
      "tilldelad",
      { shared_with_email: email.trim().toLowerCase(), project_role: role },
      { valid_from: validFrom || new Date().toISOString().slice(0, 10) }
    );
    toast.success("Deltagaren är inbjuden");
    setEmail("");
    setIsDeputy(false);
    setValidFrom("");
    load();
  };

  const update = async (id: string, patch: Partial<Member>) => {
    const member = members.find((m) => m.id === id);
    const { error } = await supabase.from("project_shares").update(patch).eq("id", id);
    if (error) {
      toast.error("Kunde inte spara ändringen");
      return;
    }
    if (member) {
      await logHistory(
        patch.project_role ? "roll ändrad" : "uppdaterad",
        {
          shared_with_email: member.shared_with_email,
          shared_with_id: member.shared_with_id,
          project_role: patch.project_role ?? member.project_role,
        },
        {
          valid_from: patch.valid_from ?? member.valid_from,
          valid_to: patch.valid_to ?? member.valid_to,
        }
      );
    }
    load();
  };

  const remove = async (id: string) => {
    const member = members.find((m) => m.id === id);
    if (member) {
      await logHistory(
        "borttagen",
        {
          shared_with_email: member.shared_with_email,
          shared_with_id: member.shared_with_id,
          project_role: member.project_role,
        },
        { valid_to: new Date().toISOString().slice(0, 10) }
      );
    }
    const { error } = await supabase.from("project_shares").delete().eq("id", id);
    if (error) toast.error("Kunde inte ta bort deltagaren");
    else {
      toast.success("Deltagaren är borttagen");
      load();
    }
  };

  return (
    <div className="space-y-4">
      <ProjectScopeMode projectId={projectId} />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" /> Roller och behörigheter
          </CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            Som projektägare bestämmer du vilken roll varje person har. Rollen styr vad personen
            ser i projektet, och behörigheten styr om de får ändra eller bara läsa.
          </p>
        </CardHeader>
        <CardContent>
          <div className="mb-4 rounded-md border bg-muted/30 p-3">
            <p className="text-sm font-medium">Snabbmallar</p>
            <p className="mb-2 text-xs text-muted-foreground">
              Vanliga upplägg för inhyrda personer. Mallen fyller i roll, behörighet och giltighet
              – du fyller bara i e-postadressen och klickar Bjud in.
            </p>
            <div className="flex flex-wrap gap-2">
              {INVITE_TEMPLATES.map((t) => (
                <Button
                  key={t.label}
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setRole(t.role);
                    setPermission(t.permission);
                    setIsDeputy(t.isDeputy);
                    setValidFrom(new Date().toISOString().slice(0, 10));
                    toast.info(`${t.label}: ${t.hint}`);
                  }}
                >
                  {t.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-4">
            <div className="sm:col-span-2">
              <Label>E-post</Label>
              <Input
                type="email"
                placeholder="namn@foretag.se"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <Label>Roll</Label>
              <Select value={role} onValueChange={(v) => setRole(v as ProjectRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Behörighet</Label>
              <Select
                value={isReadOnlyRole(role) ? "read" : permission}
                onValueChange={setPermission}
                disabled={isReadOnlyRole(role)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="read">Läsa</SelectItem>
                  <SelectItem value="edit">Redigera</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-4">
            <div>
              <Label>Gäller från</Label>
              <Input
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                className="w-full sm:w-[170px]"
              />
            </div>
            <label className="flex items-center gap-2 pb-2 text-sm">
              <Checkbox
                checked={isDeputy}
                onCheckedChange={(v) => setIsDeputy(v === true)}
              />
              Ersättare (vikarie för ordinarie roll)
            </label>
          </div>
          <Button className="mt-3" onClick={invite}>
            <UserPlus className="mr-1 h-4 w-4" /> Bjud in
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Deltagare</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading && <p className="text-sm text-muted-foreground">Laddar…</p>}
          {!loading && members.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Inga deltagare ännu. Du är ensam ägare av projektet.
            </p>
          )}
          {members.map((m) => (
            <div
              key={m.id}
              className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{m.shared_with_email}</p>
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  <Badge variant={m.accepted ? "secondary" : "outline"}>
                    {m.accepted ? "Aktiv" : "Väntar på att inbjudan accepteras"}
                  </Badge>
                  {m.is_deputy && <Badge variant="outline">Ersättare</Badge>}
                  <span className="text-xs text-muted-foreground">
                    {fmtDate(m.valid_from)} – {m.valid_to ? fmtDate(m.valid_to) : "tills vidare"}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  value={m.project_role}
                  onValueChange={(v) =>
                    update(m.id, {
                      project_role: v,
                      ...(isReadOnlyRole(v) ? { permission_level: "read" } : {}),
                    })
                  }
                >
                  <SelectTrigger className="w-full sm:w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={
                    isReadOnlyRole(m.project_role) || m.permission_level === "read"
                      ? "read"
                      : "edit"
                  }
                  onValueChange={(v) => update(m.id, { permission_level: v })}
                  disabled={isReadOnlyRole(m.project_role)}
                >
                  <SelectTrigger className="w-full sm:w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="read">Läsa</SelectItem>
                    <SelectItem value="edit">Redigera</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="date"
                  aria-label="Gäller från"
                  value={toDateInput(m.valid_from)}
                  onChange={(e) => update(m.id, { valid_from: e.target.value || null })}
                  className="w-full sm:w-[150px]"
                />
                <Input
                  type="date"
                  aria-label="Gäller till"
                  value={toDateInput(m.valid_to)}
                  onChange={(e) => update(m.id, { valid_to: e.target.value || null })}
                  className="w-full sm:w-[150px]"
                />
                <label className="flex items-center gap-1 text-xs">
                  <Checkbox
                    checked={Boolean(m.is_deputy)}
                    onCheckedChange={(v) => update(m.id, { is_deputy: v === true })}
                  />
                  Ersättare
                </label>
                <label className="flex items-center gap-1 text-xs">
                  <Checkbox
                    checked={Boolean(m.can_export)}
                    onCheckedChange={(v) => update(m.id, { can_export: v === true })}
                  />
                  Får exportera
                </label>
                <label className="flex items-center gap-1 text-xs">
                  <Checkbox
                    checked={Boolean(m.can_invite)}
                    onCheckedChange={(v) => update(m.id, { can_invite: v === true })}
                  />
                  Får bjuda in
                </label>
                <label className="flex items-center gap-1 text-xs">
                  <Checkbox
                    checked={Boolean(m.can_share_external)}
                    onCheckedChange={(v) => update(m.id, { can_share_external: v === true })}
                  />
                  Får dela externt
                </label>
                <Button variant="ghost" size="icon" onClick={() => remove(m.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          <p className="pt-2 text-xs text-muted-foreground">
            Entreprenörer utan systemkonto bjuds in via delningslänk under fliken Entreprenörer.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4" /> Rollhistorik
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => setShowHistory((v) => !v)}>
            {showHistory ? "Dölj" : "Visa"}
          </Button>
        </CardHeader>
        {showHistory && (
          <CardContent className="space-y-2">
            {history.length === 0 && (
              <p className="text-sm text-muted-foreground">Inga rolländringar loggade ännu.</p>
            )}
            {history.map((h) => (
              <div key={h.id} className="rounded-md border p-2 text-sm">
                <span className="font-medium">{h.person_email}</span> – {h.project_role} (
                {h.action})
                <div className="text-xs text-muted-foreground">
                  {new Date(h.created_at).toLocaleString("sv-SE")} · {fmtDate(h.valid_from)} –{" "}
                  {h.valid_to ? fmtDate(h.valid_to) : "tills vidare"}
                </div>
              </div>
            ))}
          </CardContent>
        )}
      </Card>
      <RoleCompetence projectId={projectId} />
    </div>
  );
};

export default ProjectMembers;
